import { Component, ComponentChild, h } from 'preact'
import InputGroup from '../../control-components/ui-components/InputGroup'
import Axios from 'axios'
import { Group } from '../groups-destination/GroupOverview.sync'
import { arrayMove, SortableContainer, SortableElement } from 'react-sortable-hoc'
import InputGroupWithIcons from '../../control-components/ui-components/InputGroupWithIcons'
import Icon from '../assets/index'
import DragHandle from '../control/DragHandle'

interface NewData {
  title: string
  groups: Group[]
  buttonLabel?: string
  buttonURL?: string
}

interface Props {
  title: string
  selectedGroups: Group[]
  buttonLabel?: string
  buttonURL?: string
  onChange: (newData: NewData) => void
}

interface State {
  title: string
  filterText: string
  inputInFocus: boolean
  allGroups: Group[]
  selectedGroups: Group[]
  filteredGroups: Group[]
  remainingGroupsAfterSelection: Group[]
  buttonLabel?: string
  buttonURL?: string
}

const SortableList = SortableContainer(({ children }: { children: ComponentChild }) => (
  <ul className='sortable-groups-list'>{children}</ul>
))
const SortableItem = SortableElement(({ children }: { children: ComponentChild }) => (
  <li className='sortable-group'>{children}</li>
))

export class GroupsWidgetConfigurator extends Component<Props, State> {
  constructor (props: Props) {
    super(props)

    this.state = {
      title: props.title,
      filterText: '',
      inputInFocus: false,
      allGroups: [],
      selectedGroups: props.selectedGroups || [],
      filteredGroups: [],
      remainingGroupsAfterSelection: [],
      buttonLabel: props.buttonLabel || '',
      buttonURL: props.buttonURL || ''
    }

    this.onChangeTitle = this.onChangeTitle.bind(this)
    this.setDropdownItems = this.setDropdownItems.bind(this)
    this.setSelectedGroups = this.setSelectedGroups.bind(this)
    this.reorderGroups = this.reorderGroups.bind(this)
    this.onChangeButtonLabel = this.onChangeButtonLabel.bind(this)
    this.onChangeButtonURL = this.onChangeButtonURL.bind(this)
    this.onChangeSend = this.onChangeSend.bind(this)
    this.removeSelectedGroup = this.removeSelectedGroup.bind(this)
  }

  onChangeTitle (newTitle: string) {
    this.setState({
      title: newTitle
    }, this.onChangeSend)
  }

  setSelectedGroups (selectedGroup: Group) {
    const selectedGroupIndex = this.state.remainingGroupsAfterSelection.findIndex(group => selectedGroup.id === group.id)
    const newRemainingGroups = this.state.remainingGroupsAfterSelection.concat([])
    newRemainingGroups.splice(selectedGroupIndex, 1)
    this.setState({
      selectedGroups: this.state.selectedGroups.concat(selectedGroup),
      remainingGroupsAfterSelection: newRemainingGroups,
      filterText: ''
    }, this.onChangeSend)
  }

  removeSelectedGroup (idx: number, groupToDelete: Group) {
    const newSelectedGroups = this.state.selectedGroups.concat([])
    newSelectedGroups.splice(idx, 1)
    const newRemainingGroups = this.state.remainingGroupsAfterSelection.concat([])
    newRemainingGroups.push(groupToDelete)
    this.setState({ selectedGroups: newSelectedGroups, remainingGroupsAfterSelection: newRemainingGroups }, this.onChangeSend)
  }

  onChangeButtonLabel (newButtonLabel: string) {
    this.setState({
      buttonLabel: newButtonLabel
    }, this.onChangeSend)
  }

  onChangeButtonURL (newButtonURL: string) {
    this.setState({
      buttonURL: newButtonURL
    }, this.onChangeSend)
  }

  onChangeSend () {
    const newData: NewData = {
      title: this.state.title,
      groups: this.state.selectedGroups,
      buttonLabel: this.state.buttonLabel,
      buttonURL: this.state.buttonURL
    }

    this.props.onChange(newData)
  }

  setDropdownItems (filterValue: string) {
    if (/^https?/.test(filterValue)) {
      const filteredGroups = this.state.remainingGroupsAfterSelection.filter(group => group.url.toLowerCase().includes(filterValue.toLowerCase()))
      this.setState({ filteredGroups: filteredGroups })
    } else {
      const filteredGroups = this.state.remainingGroupsAfterSelection.filter(group => group.title.toLowerCase().includes(filterValue.toLowerCase()))
      this.setState({ filteredGroups: filteredGroups })
    }
  }

  reorderGroups (result: {oldIndex: number, newIndex: number}) {
    const newOrder = arrayMove(this.state.selectedGroups, result.oldIndex, result.newIndex)
    this.setState({ selectedGroups: newOrder }, this.onChangeSend)
  }

  async componentDidMount () {
    const res = await Axios.get('/getGroups')
    if (res.data) {
      const allGroups = res.data.myGroups.map((myGroup: Group) => { myGroup.isUserMember = true; return myGroup })
        .concat(res.data.otherGroups)

      const remainingGroupsAfterSelection = allGroups.concat([])

      for (const selectedGroup of this.props.selectedGroups) {
        const idx = remainingGroupsAfterSelection.findIndex((group: Group) => group.id === selectedGroup.id)
        remainingGroupsAfterSelection.splice(idx, 1)
      }

      this.setState({ allGroups: allGroups, remainingGroupsAfterSelection: remainingGroupsAfterSelection })
    }
  }

  render (_: Props, { title, selectedGroups, filteredGroups, filterText, inputInFocus, buttonLabel, buttonURL }: State) {
    return (
      <div class='groups-widget__configurator'>
        <InputGroup
          title='Title'
          type='text'
          qaClasses='groups-widget__title'
          onChange={(e: {target: HTMLInputElement}) => { this.onChangeTitle(e.target.value) }}
          value={title}
        />
        <div>
          <strong>Groups</strong>
        </div>
        <SortableList
          useDragHandle
          onSortEnd={this.reorderGroups}
        >
          {
            selectedGroups.map((selectedGroup, idx) => (
              <SortableItem index={idx} key={selectedGroup.id}>
                <div>
                  <DragHandle />
                  <div className='sortable-group_title'>{selectedGroup.title}</div>
                  <div className='sortable-group_delete-icon' onClick={() => this.removeSelectedGroup(idx, selectedGroup)}>
                    <Icon color='#2B3346' icon='trash' />
                  </div>
                </div>
              </SortableItem>
            ))
          }
          <div className='groups-widget-add-group-wrapper'>
            <InputGroupWithIcons
              attributes={{
                type: 'text',
                placeholder: 'Search for groups or enter a group URL',
                value: filterText
              }}
              className='groups-widget-add-input-wrapper'
              preIcon={<Icon color='#6B707E' icon='add' />}
              onChange={(newValue: string) => { this.setState({ filterText: newValue }, () => this.setDropdownItems(newValue)) }}
              onFocus={() => this.setState({ inputInFocus: true }, () => this.setDropdownItems(filterText))}
              onBlur={() => setTimeout(() => {
                this.setState({ inputInFocus: false })
              }, 100)}
            />
            {
              inputInFocus
                ? (
                  <ul className='groups-widget-dropdown-list'>
                    {
                    filteredGroups.length
                      ? (
                          filteredGroups.map((filteredGroup: Group) => (
                            <li
                              key={filteredGroup.id}
                              className='groups-widget-dropdown-list_item'
                              onMouseDown={() => this.setSelectedGroups(filteredGroup)}
                            >{filteredGroup.title}</li>
                          ))
                        )
                      : <li className='groups-widget-dropdown-list_item'>No Match</li>
                  }
                  </ul>
                  )
                : null
            }
          </div>
        </SortableList>
        <div className='groups-widget__buttons'>
          <InputGroup
            title='Button label (optional)'
            type='text'
            placeholder='Show more'
            onChange={(e: { target: HTMLInputElement }) => {
              this.onChangeButtonLabel(e.target.value)
            }}
            value={buttonLabel}
          />
          <InputGroup
            title='URL (optional)'
            type='url'
            placeholder='https://example.com'
            onChange={(e: { target: HTMLInputElement }) => {
              this.onChangeButtonURL(e.target.value)
            }}
            value={buttonURL}
          />
        </div>
      </div>
    )
  }
}

export default GroupsWidgetConfigurator
